/**
 * lib/cards/template-minimal.js — Minimal card template.
 *
 * Chunk 2 refactor (Apr 24 2026): layout object is now built by
 * buildMinimalLayout() in build-runtime-layout.js — same function
 * the Konva preview uses. This guarantees preview and print stay in
 * sync.
 *
 * This file is now purely the PRINT path:
 *   1. Async content resolution (image loads, QR generation)
 *   2. Background + pre-wave elements (via renderLayoutTo)
 *   3. Fallback painting for missing images
 *   4. Procedural wave band
 *   5. Footer text (via renderLayoutTo)
 *
 * Geometry unchanged from Chunk 1. Byte-identical output preserved.
 */

import { jsPDF } from 'jspdf';
import {
  CR80,
  generateQR,
  verifyUrl,
  loadImageAsDataUrl,
  deriveInitials,
  drawPhotoPlaceholder,
} from './shared.js';
import { drawWaveBand } from './defaults/wave.js';
import { renderLayoutTo } from './render-layout.js';
import { buildMinimalLayout } from './build-runtime-layout.js';

export async function renderMinimalCard({ staff, tenant, staffSlug }) {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [CR80.widthMm, CR80.heightMm],
  });

  // ── Build layout via shared helper ──
  const { layout, derived } = buildMinimalLayout(tenant, staff);

  // ── Resolve async content ──
  const contentMap = {};
  const initials = deriveInitials(staff.first_name, staff.last_name);

  if (tenant.logo_url) {
    try {
      const { dataUrl, width, height } = await loadImageAsDataUrl(tenant.logo_url);
      contentMap.el_logo = { imageData: dataUrl, width, height, format: 'PNG' };
    } catch (e) {
      console.warn('[minimal] logo load failed, text fallback:', e?.message || e);
    }
  }

  if (staff.photo_url) {
    try {
      const { dataUrl } = await loadImageAsDataUrl(staff.photo_url);
      contentMap.el_staff_photo = { imageData: dataUrl, format: 'PNG' };
    } catch (e) {
      console.warn('[minimal] photo load failed, placeholder:', e?.message || e);
    }
  }

  const qrUrl = verifyUrl(tenant.slug, staffSlug);
  contentMap.el_qr = { qrDataUrl: await generateQR(qrUrl, 512) };

  contentMap.el_name = { text: derived.displayName };
  if (staff.role) contentMap.el_role = { text: staff.role };
  if (derived.hasFooter) contentMap.el_footer_text = { text: derived.footerText };

  // ── 1. Background ──
  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, CR80.widthMm, CR80.heightMm, 'F');

  // ── 2. Pre-wave elements (z < 20) ──
  const preWaveLayout = {
    ...layout,
    elements: layout.elements.filter((el) => (el.z || 0) < 20),
  };
  await renderLayoutTo(pdf, preWaveLayout, contentMap, {
    brandColor: derived.brandColor,
    tenant,
    staff,
    skipBackground: true,
  });

  // ── 2a. Logo text fallback (if image failed to load) ──
  if (!contentMap.el_logo) {
    const f = derived.logoFallback;
    pdf.setFontSize(f.fontSize);
    pdf.setFont('helvetica', f.fontWeight);
    pdf.setTextColor(14, 26, 21);
    pdf.text(f.text, f.x, f.y);
  }

  // ── 2b. Photo placeholder (if image failed to load) ──
  if (!contentMap.el_staff_photo) {
    const p = derived.photoPlaceholder;
    drawPhotoPlaceholder(pdf, p.x, p.y, p.w, p.h, initials);
  }

  // ── 3. Wave band (procedural) ──
  drawWaveBand(pdf, derived.brandColor, derived.waveBand.topY);

  // ── 4. Footer text (on top of wave) ──
  if (contentMap.el_footer_text) {
    const footerLayout = {
      ...layout,
      elements: layout.elements.filter((el) => (el.z || 0) >= 20),
    };
    await renderLayoutTo(pdf, footerLayout, contentMap, {
      brandColor: derived.brandColor,
      tenant,
      staff,
      skipBackground: true,
    });
  }

  return pdf;
}
